import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Fetch single employee by ID
export const fetchEmployeeById = createAsyncThunk(
  'employeeDetail/fetchEmployeeById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.response?.data || 'Failed to fetch employee');
    }
  }
);

// Create a new employee
export const createEmployee = createAsyncThunk(
  'employeeDetail/createEmployee',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/employee`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.response?.data || 'Failed to create employee');
    }
  }
);

// Update employee
export const updateEmployeeById = createAsyncThunk(
  'employeeDetail/updateEmployeeById',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/employee/${id}`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.response?.data || 'Failed to update employee');
    }
  }
);

// Check Employee Completeness (PATCH API)
export const checkEmployeeCompleteness = createAsyncThunk(
  'employeeDetail/checkEmployeeCompleteness',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/employee/${id}/check-completeness`);
      return response.data; // Usually returns the updated status or list of missing requirements
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.response?.data || 'Failed to check completeness');
    }
  }
);

const employeeDetailSlice = createSlice({
  name: 'employeeDetail',
  initialState: {
    employee: null,
    status: 'idle', 
    completenessStatus: 'idle', // Track completeness API specifically
    error: null,
  },
  reducers: {
    clearEmployee: (state) => {
      state.employee = null;
      state.status = 'idle';
      state.completenessStatus = 'idle';
      state.error = null;
    },
    setEmployeeLocal: (state, action) => {
      state.employee = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      /* Fetch Case */
      .addCase(fetchEmployeeById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employee = action.payload;
      })
      .addCase(fetchEmployeeById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload; 
      })

      /* Create Case */
      .addCase(createEmployee.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employee = action.payload;
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      /* Update Case */
      .addCase(updateEmployeeById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateEmployeeById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employee = action.payload;
      })
      .addCase(updateEmployeeById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      /* Check Completeness Case */
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = 'loading';
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state, action) => {
        state.completenessStatus = 'succeeded';
        // If the API returns the updated employee object, update it here
        if (action.payload && action.payload.employeeStatus) {
           state.employee = { ...state.employee, employeeStatus: action.payload.employeeStatus };
        }
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearEmployee, setEmployeeLocal } = employeeDetailSlice.actions;
export default employeeDetailSlice.reducer;